import { useCallback } from 'react';
import { isSalesforceProfileEventsEnabled, sendKnownIdentity } from './salesforceInteractions';

export interface SalesforceIdentityName {
    firstName?: string;
    lastName?: string;
}

/**
 * Hook to tell the Salesforce Data Cloud Web SDK who the visitor is, after a
 * successful form submission.
 *
 * With NEXT_PUBLIC_SF_PROFILE_EVENTS on, this sends two Data 360 profile events:
 * `identity` (`isAnonymous: 0`, first and last name) and `contactPointEmail`
 * (the email address, which identity resolution matches to a Contact). It waits for the SDK to finish initialising (one pending
 * identity at most), is dropped when there is no SDK on the page, and never
 * sends unless the visitor has opted in to tracking. The `eventType` argument
 * is only used by the legacy event below; profile events carry no form label.
 *
 * With the flag off it keeps sending the original `Identity` interaction.
 */
export function useSalesforceIdentity() {
    const sendIdentityEvent = useCallback(
        (email: string, eventType: string = 'form_submission', name: SalesforceIdentityName = {}) => {
            // Guard against SSR
            if (typeof window === 'undefined') return;

            if (isSalesforceProfileEventsEnabled()) {
                // Callers run this right after the lead was created, so a
                // tracking failure must never turn into a form error.
                try {
                    sendKnownIdentity({ email, firstName: name.firstName, lastName: name.lastName });
                } catch (error) {
                    console.warn('Salesforce identity event failed:', error);
                }
                return;
            }

            // Guard against missing SDK
            if (window.SalesforceInteractions) {
                window.SalesforceInteractions.sendEvent({
                    interaction: {
                        name: 'Identity',
                    },
                    user: {
                        identities: {
                            email: email,
                        },
                        attributes: {
                            eventType: eventType,
                        },
                    },
                });
            }
        },
        [],
    );

    return { sendIdentityEvent };
}
